import React, { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';

import Icon from '../icon/Icon';
import { useTheme } from '../../theme';
import { useEventCallback } from '../../../common/utils/hooks';

// Round tick (32) inside a 44+ pt touch area, matching TickRow: every item
// list tick (pick lists, planogram picks, service run machines) looks the same.
const BOX_STYLE = {
	borderRadius: 999,
	borderWidth: 2,
	height: 32,
	width: 32,
	alignItems: 'center',
	justifyContent: 'center',
};

const Unchecked = ({ colors }) => {
	return (
		<View style={[BOX_STYLE, { borderColor: colors.iconMuted }]} />
	);
};

const Checked = ({ colors }) => {
	return (
		<View style={[BOX_STYLE, { borderColor: colors.primary, backgroundColor: colors.primary }]}>
			<Icon size={18} name="check" color={colors.onPrimary} />
		</View>
	);
};

const PartiallyChecked = ({ colors }) => {
	return (
		<View style={[BOX_STYLE, { borderColor: colors.primary, backgroundColor: colors.primary, padding: 2 }]}>
			<View style={{
				borderRadius: 1,
				backgroundColor: colors.onPrimary,
				height: 4,
				width: '60%',
			}}
			/>
		</View>
	);
};

const Disabled = ({ colors }) => {
	return (
		<View style={[BOX_STYLE, { borderColor: colors.disabledBg, backgroundColor: colors.disabledBg }]} />
	);
};

// readOnly: shows the state (checked, partly checked) without reacting to taps.
const Toggle = ({
	disabled,
	readOnly = false,
	enabled,
	partiallyEnabled,
	onChange,
}) => {
	const { theme: { ui: { colors } } } = useTheme();

	const [isEnabled, setIsEnabled] = useState(enabled);
	const [isPartiallyEnabled, setIsPartiallyEnabled] = useState(partiallyEnabled);

	const handleUncontrolledToggle = useEventCallback(() => {
		if (disabled || readOnly) {
			return null;
		}
		setIsEnabled(!isEnabled);
		setIsPartiallyEnabled(false);
		if (onChange) {
			onChange(!enabled);
		}
	});

	return (
		<TouchableOpacity
			onPress={handleUncontrolledToggle}
			disabled={readOnly}
			style={{ padding: 8 }}
			accessibilityRole="checkbox"
			accessibilityState={{
				checked: isPartiallyEnabled ? 'mixed' : Boolean(isEnabled),
				disabled: Boolean(disabled || readOnly),
			}}
		>
			{ isPartiallyEnabled
				? <PartiallyChecked colors={colors} />
				: disabled
					? <Disabled colors={colors} />
					: isEnabled
						? <Checked colors={colors} />
						: <Unchecked colors={colors} />}
		</TouchableOpacity>
	);
};

export default Toggle;
